import { useEffect, useMemo, useState } from 'react';
import { Image, message, Space } from 'antd';
import OrderService from 'services/order';
import StoreSettingService from 'services/store_setting';
import { useApp } from 'contexts/app';
import { formatCurrency } from 'utils/number';
import { formatDate } from 'utils/date';
import { useNavigate, useSearchParams } from 'react-router';
import dayjs from 'dayjs';
import _ from 'lodash';

const service = new OrderService();
const storeSettingService = new StoreSettingService();

const shippingStatusOptions = [
  { id: 'not_shipped', title: 'Chờ vận chuyển' },
  { id: 'shipping', title: 'Đang vận chuyển' },
  { id: 'shipped', title: 'Đã giao' },
];

const paymentStatusOptions = [
  { id: 'all', title: 'Tất cả' },
  { id: 'unpaid', title: 'Chờ thanh toán' },
  { id: 'partial', title: 'Thanh toán 1 phần' },
  { id: 'paid', title: 'Đã thanh toán' },
  { id: 'waiting_verification', title: 'Chờ xác nhận thanh toán' },
];

const getLineItemImageUrl = (line) => {
  const image = line?.variant?.image
    || line?.variant?.images?.[0]
    || line?.product?.thumbnail
    || line?.product?.image
    || line?.product?.images?.[0]
    || line?.image
    || line?.images?.[0]
    || null;

  if (!image) return null;
  if (typeof image === 'string') return image;
  return image?.url || null;
};

const useList = () => {
  const navigate = useNavigate();
  const { showConfirm } = useApp();
  const [data, setData] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkShippingModalOpen, setBulkShippingModalOpen] = useState(false);
  const [bulkShippingStatus, setBulkShippingStatus] = useState('not_shipped');
  const [bulkInternalModalOpen, setBulkInternalModalOpen] = useState(false);
  const [bulkInternalStatus, setBulkInternalStatus] = useState(undefined);
  const [internalStatuses, setInternalStatuses] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const getCurrentSearParams = () => {
    const params = JSON.parse(searchParams.get('q')) ?? {};
    if (!params.status) {
      params.status = 'proccesing';
    }
    return params;
  };

  const searchPr = getCurrentSearParams();
  const [filter, setFilter] = useState(searchPr);

  useEffect(() => {
    if (!_.isEqual(searchPr, filter)) {
      setFilter(searchPr);
    }
  }, []);

  useEffect(() => {
    delaySearch(filter);
    const currentSearch = getCurrentSearParams();

    if (!_.isEqual(currentSearch, filter)) {
      setSearchParams({ q: JSON.stringify(filter) });
    }
  }, [filter]);

  useEffect(() => {
    loadInternalStatuses();
  }, []);

  const loadData = async (postData) => {
    const lst = await service.list({ ...postData });
    setData(lst);
  };

  const delaySearch = useMemo(() => _.debounce(loadData, 500), []);

  const onSearch = () => {
    delaySearch({});
  };

  const setFilterValue = (name, value) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      [name]: value,
    }));
  };

  const onDateRangeFilterChange = (e) => {
    if (!e) {
      setFilterValue('create_on', null);
    } else {
      setFilterValue('create_on', {
        start: e[0].startOf('d').format(),
        end: e[1].startOf('d').format(),
      });
    }

    console.log('e', e);
  };

  const loadInternalStatuses = async () => {
    try {
      const statuses = await storeSettingService.getOrderInternalStatuses();
      if (_.isArray(statuses) && statuses.length > 0) {
        setInternalStatuses(statuses);
        setBulkInternalStatus(statuses?.[0]?.id);
      }
    } catch (error) {
    }
  };

  const getSelectedIDs = () => (selectedOrderIds || []).map(String).filter(Boolean);

  const printPdf = (url) => {
    fetch(url).then((response) => {
      return response.blob().then(function (myBlob) {
        const objectURL = URL.createObjectURL(myBlob);
        const iframe = document.createElement('iframe');
        document.body.appendChild(iframe);
        iframe.style.display = 'none';
        iframe.onload = function () {
          setTimeout(function () {
            iframe.focus();
            iframe.contentWindow.print();
          }, 1);
        };
        iframe.src = objectURL;
      });
    });
  };

  const runBulkUpdateShippingStatus = async () => {
    const ids = getSelectedIDs();
    if (!ids.length) {
      message.warning('Vui lòng chọn đơn hàng');
      return;
    }

    try {
      setBulkActionLoading(true);
      await service.bulkUpdateShippingStatus({ ids, shipping_status_id: bulkShippingStatus });
      message.success('Đã cập nhật trạng thái vận chuyển');
      setBulkShippingModalOpen(false);
      setSelectedOrderIds([]);
      await loadData(filter);
    } catch (e) {
      message.error(e?.message || 'Cập nhật trạng thái vận chuyển thất bại');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const runBulkUpdateInternalStatus = async () => {
    const ids = getSelectedIDs();
    if (!ids.length) {
      message.warning('Vui lòng chọn đơn hàng');
      return;
    }

    if (!bulkInternalStatus) {
      message.warning('Không có trạng thái nội bộ để cập nhật');
      return;
    }

    try {
      setBulkActionLoading(true);
      await service.bulkUpdateInternalStatus({ ids, internal_status_id: bulkInternalStatus });
      message.success('Đã cập nhật trạng thái nội bộ');
      setBulkInternalModalOpen(false);
      setSelectedOrderIds([]);
      await loadData(filter);
    } catch (e) {
      message.error(e?.message || 'Cập nhật trạng thái nội bộ thất bại');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const runBulkPackingList = async () => {
    const ids = getSelectedIDs();
    if (!ids.length) {
      message.warning('Vui lòng chọn đơn hàng');
      return;
    }

    try {
      setBulkActionLoading(true);
      const file = await service.bulkCreatePackingListFile({ ids });
      if (!file?.file) {
        throw new Error('Không tạo được phiếu đóng gói tổng hợp');
      }
      printPdf(file.file);
      message.success('Đã tạo phiếu đóng gói tổng hợp');
    } catch (e) {
      message.error(e?.message || 'Tạo phiếu đóng gói hàng loạt thất bại');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const runBulkShippingLabel = async () => {
    const ids = getSelectedIDs();
    if (!ids.length) {
      message.warning('Vui lòng chọn đơn hàng');
      return;
    }

    try {
      setBulkActionLoading(true);
      const file = await service.bulkCreateShippingLabelFile({ ids });
      if (!file?.file) {
        throw new Error('Không tạo được nhãn vận chuyển');
      }
      printPdf(file.file);
      message.success('Đã tạo nhãn vận chuyển hàng loạt');
    } catch (e) {
      message.error(e?.message || 'Tạo nhãn vận chuyển hàng loạt thất bại');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const onBulkActionSelect = ({ key }) => {
    if (key === 'shipping_status') {
      setBulkShippingModalOpen(true);
      return;
    }

    if (key === 'internal_status') {
      setBulkInternalModalOpen(true);
      return;
    }

    if (key === 'packing_list') {
      showConfirm({
        title: 'Tạo phiếu đóng gói',
        content: `Bạn có chắc muốn tạo phiếu đóng gói cho ${selectedOrderIds.length} đơn hàng đã chọn?`,
        okText: 'Đồng ý',
        cancelText: 'Hủy',
        onOk: () => runBulkPackingList(),
      });
      return;
    }

    if (key === 'shipping_label') {
      showConfirm({
        title: 'Tạo nhãn vận chuyển',
        content: `Bạn có chắc muốn tạo nhãn vận chuyển cho ${selectedOrderIds.length} đơn hàng đã chọn?`,
        okText: 'Đồng ý',
        cancelText: 'Hủy',
        onOk: () => runBulkShippingLabel(),
      });
    }
  };

  const getInternalStatusTitle = (record) => {
    const internalStatusId = record?.internal_status?.id;
    if (!internalStatusId) {
      return '-';
    }

    const item = (internalStatuses || []).find((x) => x?.id === internalStatusId);
    return item?.title || record?.internal_status?.title || internalStatusId;
  };

  const getOrderImageUrls = (record) => {
    const lineItems = Array.isArray(record?.line_items) ? record.line_items : [];
    return lineItems.map(getLineItemImageUrl).filter(Boolean);
  };

  const columns = [
    {
      title: 'Mã đơn hàng',
      render: (record) => {
        return (
          <>
            <button type='button' style={{ border: 'none', background: 'none', padding: 0, color: '#1677ff', cursor: 'pointer' }} onClick={() => navigate(`/order/${record.id}`)}>
              {record.id}
            </button>
            <div>{formatDate(record.create_on)}</div>
          </>
        );
      },
    },
    {
      title: 'Khách hàng',
      render: (record) => {
        return (
          <>
            <div>{record.customer?.phone}</div>
            <div>{record.customer?.full_name}</div>
          </>
        );
      },
    },
    {
      title: 'Sản phẩm',
      render: (record) => {
        const imageUrls = getOrderImageUrls(record);
        const maxVisibleImages = 3;
        const visibleImages = imageUrls.slice(0, maxVisibleImages);
        const hiddenCount = imageUrls.length - visibleImages.length;

        return (
          <>
            <Image.PreviewGroup items={imageUrls}>
              <Space size={4} wrap>
                {visibleImages.map((url, index) => (
                  <Image
                    key={`${record.id || 'order'}-${index}`}
                    src={url}
                    width={34}
                    height={34}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                    fallback='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzNCIgaGVpZ2h0PSIzNCI+PHJlY3Qgd2lkdGg9IjM0IiBoZWlnaHQ9IjM0IiBmaWxsPSIjZjVmNWY1Ii8+PC9zdmc+'
                  />
                ))}
                {hiddenCount > 0 && (
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 6,
                      background: '#f5f5f5',
                      color: '#595959',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                    }}
                  >
                    +{hiddenCount}
                  </div>
                )}
              </Space>
            </Image.PreviewGroup>
            <div>{record.total_item} sản phẩm</div>
            <div>{formatCurrency(record.total_amount)}</div>
          </>
        );
      },
    },
    {
      title: 'Trạng thái',
      render: (record) => {
        return (
          <>
            {record.status == 'canceled' && <div>Đã Huỷ</div>}

            {record.status == 'finalized' && <div>Đã Hoàn tất</div>}

            {!['canceled', 'finalized'].includes(record.status) && (
              <>
                <div>{record.payment_status.title}</div>
                <div>{record.shipping_status.title}</div>
                <div>{getInternalStatusTitle(record)}</div>
              </>
            )}
          </>
        );
      },
    },
  ];

  const rangePickerPresets = [
    { label: 'Hôm nay', value: [dayjs().add(0, 'd'), dayjs().add(0, 'd')] },
    { label: 'Hôm qua', value: [dayjs().add(-1, 'd'), dayjs().add(-1, 'd')] },
    { label: '7 ngày qua', value: [dayjs().add(-7, 'd'), dayjs().add(0, 'd')] },
    { label: '30 ngày qua', value: [dayjs().add(30, 'd'), dayjs().add(0, 'd')] },
  ];

  const rangePickerValue = filter.create_on ? [dayjs(filter.create_on.start), dayjs(filter.create_on.end)] : null;

  const summary = {
    totalOrders: data.length,
    totalAmount: _.sumBy(data, (x) => x.total_amount),
  };

  const bulkMenuItems = [
    { key: 'shipping_status', label: 'Cập nhật trạng thái vận chuyển' },
    { key: 'internal_status', label: 'Cập nhật trạng thái nội bộ' },
    { key: 'packing_list', label: 'Phiếu đóng gói' },
    { key: 'shipping_label', label: 'Nhãn vận chuyển' },
  ];

  return {
    data,
    filter,
    setFilterValue,
    onDateRangeFilterChange,
    onSearch,
    navigate,
    columns,
    rangePickerPresets,
    rangePickerValue,
    summary,
    paymentStatusOptions,
    shippingStatusOptions,
    internalStatuses,
    selectedOrderIds,
    setSelectedOrderIds,
    bulkActionLoading,
    bulkMenuItems,
    onBulkActionSelect,
    bulkShippingModalOpen,
    setBulkShippingModalOpen,
    bulkShippingStatus,
    setBulkShippingStatus,
    runBulkUpdateShippingStatus,
    bulkInternalModalOpen,
    setBulkInternalModalOpen,
    bulkInternalStatus,
    setBulkInternalStatus,
    runBulkUpdateInternalStatus,
    getOrderImageUrls,
  };
};

export default useList;
